import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { initializeApp, getApp, getApps } from 'firebase/app'
import { useOnboardingStore } from '../store/onboardingStore'
import { Button } from '../components/Button'
import { calculateNutrientLimits } from '../lib/nutrients'
import {
  MealAnalysisResult,
  type AnalysisResult,
  type AnalysisFood,
  type AnalysisSwap,
  type ExtraNutrients,
} from '../components/MealAnalysisResult'

/**
 * After the canned DemoScan, the visitor scans their OWN food (photo or text).
 * Calls the unauth `analyzeMealDemo` / `analyzeFoodDemo` cloud functions — the
 * same backend the in-app onboarding uses. The displayed results match the
 * app's analysis screen via the shared MealAnalysisResult component so the
 * funnel feels continuous with the product.
 *
 * Skippable so visitors who don't want to bother can move on.
 */

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyC9lSvdpQKR1XEsDKQ9mc6EfjaVjkZYreU',
  authDomain: 'kidneyrecipes.firebaseapp.com',
  projectId: 'kidneyrecipes',
  storageBucket: 'kidneyrecipes.firebasestorage.app',
  messagingSenderId: '810820724974',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:810820724974:web:4f3cd0fdb33b892f83abff',
}

const app = getApps().length ? getApp() : initializeApp(firebaseConfig)
const functions = getFunctions(app)

type Mode = 'choose' | 'analyzing' | 'results' | 'error'

interface ScanResult {
  analysis: AnalysisResult
  photoUrl?: string  // data URL of the user's photo (for display in results)
  source: 'photo' | 'text'
}

// ────────────────────────── Component ──────────────────────────

export function UserScanStep() {
  const { goNext, data } = useOnboardingStore()
  const [mode, setMode] = useState<Mode>('choose')
  const [textInput, setTextInput] = useState('')
  const [result, setResult] = useState<ScanResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Daily limits used to compute percentages on the totals display. The user
  // hasn't answered KidneyStatus/Weight/Comorbidities at this point in the
  // short flow, so calculateNutrientLimits returns conservative defaults.
  const limits = calculateNutrientLimits(
    data.kidneyStatus,
    data.comorbidities,
    data.weightKg,
    data.dialysisType,
  )

  const buildKidneyProfile = () => ({
    kidneyStatus: data.kidneyStatus || undefined,
  })

  const handlePhotoSelect = async (file: File) => {
    if (file.size > 8 * 1024 * 1024) {
      setErrorMessage('That photo is too large — please use one under 8MB.')
      setMode('error')
      return
    }

    setMode('analyzing')
    try {
      const base64 = await fileToBase64(file)
      const photoUrl = `data:${file.type};base64,${base64}`
      const analyzeMealDemo = httpsCallable(functions, 'analyzeMealDemo')
      const response = await analyzeMealDemo({
        imageBase64: base64,
        kidneyProfile: buildKidneyProfile(),
      })
      const body = (response.data || {}) as MealResponseShape

      if (body.notFood) {
        setErrorMessage(
          body.notFoodReason ||
            "That photo doesn't look like food. Try again with a clear picture of a meal.",
        )
        setMode('error')
        return
      }

      if (!body.success || !body.analysis) {
        setErrorMessage(
          body.error || "We couldn't analyze that photo. Try a clearer picture or describe it in text.",
        )
        setMode('error')
        return
      }

      setResult({
        analysis: normalizeMealAnalysis(body.analysis, body.suggestions?.[0]),
        photoUrl,
        source: 'photo',
      })
      setMode('results')
    } catch (err) {
      console.error('Photo analysis failed:', err)
      setErrorMessage("We couldn't reach the analyzer. Try again, or describe your meal in text instead.")
      setMode('error')
    }
  }

  const handleTextSubmit = async () => {
    if (!textInput.trim()) return
    setMode('analyzing')
    try {
      const analyzeFoodDemo = httpsCallable(functions, 'analyzeFoodDemo')
      const response = await analyzeFoodDemo({
        input: textInput.trim(),
        kidneyProfile: buildKidneyProfile(),
      })
      const body = (response.data || {}) as MealResponseShape

      if (body.notFood) {
        setErrorMessage(
          body.notFoodReason || "We didn't recognize any food in that description. Try something like 'turkey sandwich on wheat with cheese'.",
        )
        setMode('error')
        return
      }

      if (!body.success || !body.analysis) {
        setErrorMessage(
          body.error || "We couldn't analyze that. Try describing it differently or skip ahead.",
        )
        setMode('error')
        return
      }

      // analyzeFoodDemo now returns the SAME meal-shape as analyzeMealDemo
      // (post-Phase-2 deterministic scoring refactor 2026-06-07), so we can
      // use the meal normalizer for both.
      setResult({
        analysis: normalizeMealAnalysis(body.analysis, body.suggestions?.[0]),
        source: 'text',
      })
      setMode('results')
    } catch (err) {
      console.error('Text analysis failed:', err)
      setErrorMessage("We couldn't reach the analyzer. Try again or skip ahead.")
      setMode('error')
    }
  }

  const resetToChoose = () => {
    setMode('choose')
    setResult(null)
    setTextInput('')
  }

  return (
    <div className="min-h-dvh flex flex-col bg-white">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[600px] mx-auto px-6 py-8">
          <AnimatePresence mode="wait">
            {mode === 'choose' && (
              <ChoosePhase
                key="choose"
                textInput={textInput}
                onTextChange={setTextInput}
                onTextSubmit={handleTextSubmit}
                onPickPhoto={() => fileInputRef.current?.click()}
                onSkip={() => goNext()}
              />
            )}
            {mode === 'analyzing' && <AnalyzingPhase key="analyzing" />}
            {mode === 'results' && result && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <MealAnalysisResult
                  result={result.analysis}
                  limits={limits}
                  mealPhotoUrl={result.photoUrl}
                  sourceLabel={result.source === 'photo' ? '📸 From your photo' : '✍️ From your description'}
                  onContinue={() => goNext()}
                  continueLabel="Continue"
                  onTryAnother={resetToChoose}
                  tryAnotherLabel="Try Another"
                />
                <div className="mt-4 text-center px-2">
                  <p className="text-sm text-gray-600">
                    <strong>This is what KidneyPal does for every meal you eat.</strong>{' '}
                    Let's build your personalized plan.
                  </p>
                </div>
              </motion.div>
            )}
            {mode === 'error' && (
              <ErrorPhase
                key="error"
                message={errorMessage}
                onRetry={resetToChoose}
                onSkip={() => goNext()}
              />
            )}
          </AnimatePresence>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handlePhotoSelect(file)
            }}
          />
        </div>
      </div>
    </div>
  )
}

// ────────────────────────── Phases ──────────────────────────

function ChoosePhase({
  textInput,
  onTextChange,
  onTextSubmit,
  onPickPhoto,
  onSkip,
}: {
  textInput: string
  onTextChange: (s: string) => void
  onTextSubmit: () => void
  onPickPhoto: () => void
  onSkip: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="text-center mb-6">
        <img src="/images/mascot-holdingveggies.png" alt="" className="w-20 h-20 object-contain mx-auto mb-3" />
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Now try it on YOUR meal
        </h1>
        <p className="text-gray-500">
          Snap a photo of what you're eating, or just type it.
        </p>
      </div>

      <button
        onClick={onPickPhoto}
        className="w-full bg-primary text-white rounded-2xl p-5 flex items-center gap-4 mb-3 hover:bg-primary/90 transition-colors shadow-md"
      >
        <span className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl shrink-0">📸</span>
        <div className="text-left flex-1">
          <p className="font-bold text-base">Take or upload a photo</p>
          <p className="text-sm text-white/80">The fastest way — our AI does the rest</p>
        </div>
        <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 font-medium">OR DESCRIBE IT</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <div className="space-y-3">
        <textarea
          value={textInput}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder="e.g. one serving of oatmeal with blueberries"
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 resize-none"
        />
        <Button onClick={onTextSubmit} disabled={!textInput.trim()}>
          Analyze this meal
        </Button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-6 py-3 text-gray-400 text-sm hover:text-gray-600 transition-colors"
      >
        Skip — keep going
      </button>
    </motion.div>
  )
}

function AnalyzingPhase() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center py-24"
    >
      <img src="/images/mascot-thinking.png" alt="" className="w-28 h-28 object-contain mb-6" />
      <h2 className="text-xl font-bold text-gray-900 mb-2">Analyzing your meal…</h2>
      <p className="text-gray-500 mb-6">Checking kidney impact, one nutrient at a time</p>
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2.5 h-2.5 rounded-full bg-primary"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
          />
        ))}
      </div>
    </motion.div>
  )
}

function ErrorPhase({
  message,
  onRetry,
  onSkip,
}: {
  message: string
  onRetry: () => void
  onSkip: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center py-12"
    >
      <div className="text-5xl mb-4">😕</div>
      <h2 className="text-xl font-bold text-gray-900 mb-3">Hmm, that didn't work</h2>
      <p className="text-gray-500 mb-6 max-w-sm mx-auto">{message}</p>
      <div className="space-y-2 max-w-sm mx-auto">
        <Button onClick={onRetry}>Try again</Button>
        <button
          onClick={onSkip}
          className="w-full py-2 text-gray-400 text-sm hover:text-gray-600 transition-colors"
        >
          Skip — keep going
        </button>
      </div>
    </motion.div>
  )
}

// ────────────────────── Response shapes (matching functions/src) ──────────────────────

interface MealResponseShape {
  success?: boolean
  notFood?: boolean
  notFoodReason?: string
  error?: string
  /** [0] is the analysis's Pro Tip. */
  suggestions?: string[]
  analysis?: {
    overallRisk?: 'safe' | 'moderate' | 'high'
    kidneyImpactScore?: number
    identifiedFoods?: Array<{
      name?: string
      portionEstimate?: string
      sodiumMg?: number
      potassiumMg?: number
      phosphorusMg?: number
      proteinG?: number
      kidneySafety?: 'safe' | 'moderate' | 'limit'
      riskLevel?: 'safe' | 'moderate' | 'high'
      primaryConcern?: string | null
    } & ExtraNutrients>
    smartSwaps?: Array<{
      original: string
      suggestion: string
      impactReduction?: string
    }>
    estimatedMealTotals?: {
      sodiumMg?: number
      potassiumMg?: number
      phosphorusMg?: number
      proteinG?: number
    } & ExtraNutrients
    reasoning?: string
  }
}

// ────────────────────────── Normalizers ──────────────────────────

function normalizeMealAnalysis(a: NonNullable<MealResponseShape['analysis']>, tip?: string): AnalysisResult {
  const foods: AnalysisFood[] = (a.identifiedFoods || []).map((f) => ({
    name: f.name || 'Item',
    portion: f.portionEstimate,
    sodiumMg: numOr(f.sodiumMg),
    potassiumMg: numOr(f.potassiumMg),
    phosphorusMg: numOr(f.phosphorusMg),
    proteinG: numOr(f.proteinG),
    safetyLevel: f.kidneySafety || mapRiskToSafety(f.riskLevel) || 'safe',
    warning: f.primaryConcern || null,
    ...pickExtras(f),
  }))

  const swaps: AnalysisSwap[] = (a.smartSwaps || []).map((s) => ({
    original: s.original,
    suggestion: s.suggestion,
    impactLabel: s.impactReduction,
  }))

  return {
    score: numOr(a.kidneyImpactScore),
    overallRisk: a.overallRisk || 'safe',
    foods,
    totals: {
      sodiumMg: numOr(a.estimatedMealTotals?.sodiumMg),
      potassiumMg: numOr(a.estimatedMealTotals?.potassiumMg),
      phosphorusMg: numOr(a.estimatedMealTotals?.phosphorusMg),
      proteinG: numOr(a.estimatedMealTotals?.proteinG),
      ...pickExtras(a.estimatedMealTotals),
    },
    swaps,
    reasoning: a.reasoning,
    tip: tip || undefined,
  }
}

const EXTRA_KEYS = ['calories', 'carbsG', 'sugarsG', 'fiberG', 'fatG', 'saturatedFatG', 'calciumMg', 'magnesiumMg', 'ironMg', 'vitaminKMcg', 'oxalateMg'] as const

/** Copy the Pro nutrients that came back as real numbers; unknown stays absent (never 0). */
function pickExtras(src?: ExtraNutrients): ExtraNutrients {
  const out: ExtraNutrients = {}
  for (const k of EXTRA_KEYS) {
    const v = src?.[k]
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
  }
  return out
}

// ────────────────────────── Helpers ──────────────────────────

function mapRiskToSafety(r?: 'safe' | 'moderate' | 'high'): 'safe' | 'moderate' | 'limit' | undefined {
  if (r === 'high') return 'limit'
  if (r === 'moderate') return 'moderate'
  if (r === 'safe') return 'safe'
  return undefined
}

function numOr(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const base64 = result.includes(',') ? result.split(',')[1] : result
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
